import { semantic } from '../colors';

import type { CSSRuleObject } from 'tailwindcss/types/config';
import type { PluginAPI } from 'tailwindcss/types/config';

type ComponentDefinition = CSSRuleObject | ((_api: PluginAPI) => CSSRuleObject);

/**
 * Just export your **component** styles from this file, and you're good to go.
 * Each component should match Tailwind's {@link CSSRuleObject}.
 *
 * Review the Tailwind documentation to learn how to write static utilities in JS:
 * https://tailwindcss.com/docs/plugins#adding-components
 */

/**
 * @revisit - Do we want these focus styles to apply globally across all elements?
 * Global focus states for all elements to ensure consistent focus behavior.
 */
export const states: ComponentDefinition = {
  'nav a:focus, p a:focus, .frc-captcha *:focus': {
    outline: 'none',
  },
  'nav a:focus-visible, p a:focus-visible, .frc-captcha *:focus-visible': {
    boxShadow: '0 0 0 2px white, 0 0 0 4px #1A65A7, 0 0 0 6px #A9D0F5',
  },
};

// Copies
export const copies: ComponentDefinition = {
  '.copy-xl': {
    fontWeight: '400',
    fontSize: '1.5rem',
    lineHeight: '1.2',
  },
};

export const headlines: ComponentDefinition = ({ theme }) => {
  return {
    '.headline-xs': {
      fontWeight: '600',
      fontSize: '0.875rem',
      lineHeight: '1.6',
    },
    '.headline-sm': {
      fontWeight: '600',
      fontSize: '1.125rem',
      lineHeight: '1.3',
    },
    '.headline-md': {
      fontWeight: '600',
      fontSize: '1.25rem',
      lineHeight: '1.3',
      [`@media (min-width: ${theme('screens.sm')})`]: {
        fontSize: '1.5rem',
      },
    },
    '.headline-lg': {
      fontWeight: '400',
      fontSize: '1.5rem',
      lineHeight: '1.2',
      [`@media (min-width: ${theme('screens.sm')})`]: {
        fontSize: '2rem',
      },
    },
    '.headline-xl': {
      fontWeight: '400',
      fontSize: '2rem',
      lineHeight: '1.3',
      [`@media (min-width: ${theme('screens.sm')})`]: {
        fontSize: '2.5rem',
        lineHeight: '1.2',
      },
    },
    '.headline-2xl': {
      fontWeight: '400',
      fontSize: '2rem',
      lineHeight: '1.3',
      [`@media (min-width: ${theme('screens.sm')})`]: {
        fontSize: '2.5rem',
        lineHeight: '1.2',
      },
      [`@media (min-width: ${theme('screens.md')})`]: {
        fontSize: '3.75rem',
        lineHeight: '1.2',
      },
    },
  };
};

// Texts
export const texts: ComponentDefinition = {
  '.text-optimize-legibility': {
    textRendering: 'optimizeLegibility',
  },
};

// Infos
export const infos: ComponentDefinition = {
  '.info-md': {
    fontWeight: '400',
    fontSize: '0.875rem',
    lineHeight: '1.625',
  },
};

// Labels
export const labels: ComponentDefinition = {
  '.label-sm': {
    fontWeight: '600',
    fontSize: '0.75rem',
    lineHeight: '1.625',
  },
  '.label-md': {
    fontWeight: '600',
    fontSize: '1rem',
    lineHeight: '1.625',
  },
};

// Link styles with responsive sizing and full-area coverage for cards
export const links: ComponentDefinition = ({ theme }) => {
  return {
    '.link-sm': {
      fontWeight: '400',
      fontSize: '0.875rem',
      lineHeight: '1.3',
      [`@media (min-width: ${theme('screens.lg')})`]: {
        fontSize: '1rem',
      },
    },
    '.link-md': {
      fontWeight: '400',
      fontSize: '1rem',
      lineHeight: '1.3',
    },
    '.faux-link-area': {
      position: 'relative',
    },
    '.faux-link-area__link': {
      position: 'absolute',
      top: '0',
      right: '0',
      bottom: '0',
      left: '0',
      zIndex: '20',
    },
    '.faux-link-area a:not(.faux-link-area__link), .faux-link-area button:not(.faux-link-area__link)':
      {
        zIndex: '21',
      },
  };
};

// Prose for Rich-text elements
export const prose: ComponentDefinition = ({ theme }) => {
  return {
    '.cf-rich-text': {
      fontSize: '1rem',
      lineHeight: '1.6',
      color: semantic.text['high-emphasis'],
    },
    // .headline-xl
    '.cf-rich-text h1:not(.no-cf-rich-text-container *):not(.no-cf-rich-text)':
      {
        fontWeight: '400',
        fontSize: '2rem',
        lineHeight: '1.3',
        [`@media (min-width: ${theme('screens.sm')})`]: {
          fontSize: '2.5rem',
          lineHeight: '1.2',
        },
      },
    // .headline-lg
    '.cf-rich-text h2:not(.no-cf-rich-text-container *):not(.no-cf-rich-text)':
      {
        fontWeight: '400',
        fontSize: '1.5rem',
        lineHeight: '1.2',
        [`@media (min-width: ${theme('screens.sm')})`]: {
          fontSize: '2rem',
        },
      },
    // .headline-md
    '.cf-rich-text > h3:not(.no-cf-rich-text-container *):not(.no-cf-rich-text)':
      {
        fontWeight: '600',
        fontSize: '1.25rem',
        lineHeight: '1.3',
        [`@media (min-width: ${theme('screens.sm')})`]: {
          fontSize: '1.5rem',
        },
      },
    // headline-sm
    '.cf-rich-text h4:not(.no-cf-rich-text-container *):not(.no-cf-rich-text), .cf-rich-text h5:not(.no-cf-rich-text-container *):not(.no-cf-rich-text), .cf-rich-text h6:not(.no-cf-rich-text-container *):not(.no-cf-rich-text)':
      {
        fontWeight: '600',
        fontSize: '1.125rem',
        lineHeight: '1.3',
      },
    '.cf-rich-text a:not(a.no-cf-rich-text):not(.no-cf-rich-text-container *):not(.no-cf-rich-text)':
      {
        color: semantic.text['high-emphasis'],
        textDecoration: 'underline',
        '&:hover': {
          textDecoration: 'none',
        },
      },
    '.cf-rich-text ul:not(.no-cf-rich-text-container *):not(.no-cf-rich-text)':
      {
        listStyleType: 'disc',
        paddingInlineStart: '1em',
      },
    '.cf-rich-text ol:not(.no-cf-rich-text-container *):not(.no-cf-rich-text)':
      {
        listStyleType: 'decimal',
        paddingInlineStart: '1em',
      },
    '.cf-rich-text li:not(.no-cf-rich-text-container *):not(.no-cf-rich-text)':
      {
        marginLeft: '1em',
      },
    '.cf-rich-text blockquote:not(.no-cf-rich-text-container *):not(.no-cf-rich-text)':
      {
        maxWidth: '40rem',
        fontSize: '1.25rem',
        lineHeight: '1.6',
        paddingLeft: '2rem',
        color: semantic.text['medium-emphasis'],
      },
    '.cf-rich-text blockquote footer:not(.no-cf-rich-text-container *):not(.no-cf-rich-text)':
      {
        fontSize: '1rem',
        fontWeight: '600',
        color: semantic.text['medium-emphasis'],
        marginTop: '0.25rem',
      },
    '.cf-rich-text > *:not(:first-child):not(.no-cf-rich-text-container *):not(.no-cf-rich-text)':
      {
        marginTop: '1rem',
      },
  };
};

// Custom Font Styles for Global Stage
// TODO: refactor & remove when Look25 is integrated
export const globalStage: ComponentDefinition = ({ theme }) => {
  return {
    '.disturber': {
      fontWeight: '400',
      fontSize: '1.25rem',
      lineHeight: '1.5',
      [`@media (min-width: ${theme('screens.sm')})`]: {
        fontSize: '1.5rem',
        lineHeight: '1.5',
      },
    },
  };
};
